import { useCallback, useEffect, useMemo, useState } from "react";
import { useFeatureFlag } from "Hooks/useFeatureFlag";
import { useFlagsmith } from 'flagsmith/react';
import { isWebGPUAvailable } from "Utils/webgpu";
import { ViewerApi } from "Data/Viewer.api";
import { IUserLimitsActive } from "Store/Auth/interfaces/IAuthState";
import { ApisEnumLabel } from "Data/enums/Apis";

/** Origem do arquivo (Google Drive, Dropbox…) legível na telemetria. */
export const apiNameForTracking = (api?: number | null): string | null =>
  api != null ? ApisEnumLabel[api] ?? String(api) : null;

export const extensions2dAnd3d = [
  'dwg', 'dxf', 'dgn', 'rvt', 'nwc', 'nwd',
  'ifc', 'dwf', 'dwfx', 'rcp',
];

export const extensions3d = [
  'rvt', 'dwf', 'nwc', 'nwd', 'ifc', 'dwfx',
  'rcp', 'skp',
];

export const allOtherExtensions = [
  'jpg', 'jpeg', 'png', 'tif', 'bmp', 'webp',
  'gif', 'doc', 'docx', 'docm', 'ppt', 'pps',
  'ppsx', 'ppsm', 'pptx', 'pptm', 'xls', 'xlsx',
  'xlsm', 'rtf', 'pdf', 'txt', 'html', 'gdoc',
  'gsheet', 'gslides',
];

export const extensionsImage = [
  'jpg', 'jpeg', 'png', 'tif', 'bmp', 'webp',
  'gif',
];

export const extensionViews2d3d = ['rvt', 'dwf', 'dwfx', 'ifc', 'rcp', 'skp'];

export const extensionPranchas = ['rvt', 'dwf', 'dwfx'];

export const extensionViewpoints = ['nwc', 'nwd'];

export const COORDLY_2D_VIEWER_EXTENSIONS = ['dwg', 'dxf'];
export const COORDLY_3D_VIEWER_EXTENSIONS = ['ifc', 'gltf', 'glb', 'obj', 'stl'];

export const useCoordlyViewerFeature = () => {
  const coordlyViewerFeatureFlag = useFeatureFlag('coordly-2d-viewer');
  const isCoordlyViewerEnabled = coordlyViewerFeatureFlag.enabled && coordlyViewerFeatureFlag.value === 'test';

  const isCoordly2DViewerEligible = (fileExtension: string) => {
    return isCoordlyViewerEnabled && COORDLY_2D_VIEWER_EXTENSIONS.indexOf(fileExtension.toLocaleLowerCase()) !== -1;
  }

  // O 3D saiu daqui: tem flag própria (`coordly-3d-viewer`) e gate próprio.
  // Fonte única = useCoordly3DViewerFeature.
  const getCoordlyViewerType = (fileExtension: string): '2D' | null => {
    return isCoordly2DViewerEligible(fileExtension) ? '2D' : null;
  }

  return {
    getCoordlyViewerType,
    isCoordly2DViewerEligible,
  }
};

export const COORDLY_3D_VIEWER_FLAG = 'coordly-3d-viewer';

export const COORDLY_3D_CLASH_FLAG = 'coordly-3d-viewer-clash';

/** Verificação de interferências no federado 3D: flag ligada com valor `test`. */
export const useCoordly3DClashFeature = (): boolean => {
  const flag = useFeatureFlag(COORDLY_3D_CLASH_FLAG);
  return !!flag?.enabled && flag?.value === 'test';
};

// Nesta fase o gate 3D vale só para IFC; as demais extensões de
// COORDLY_3D_VIEWER_EXTENSIONS são backlog do motor.
export const COORDLY_3D_VIEWER_EXTENSION = 'ifc';

/** Query string do link direto para um elemento do modelo (GlobalId do IFC). */
export const ELEMENT_GUID_PARAM = 'guid';

/**
 * Flag `coordly-3d-viewer` com valor `test` = ligada. Não há teto de tamanho: ele
 * existia quando o browser mandava o .ifc pro parser, e hoje quem converte é o
 * backend. `test-<n>` (o formato antigo, com teto) continua valendo como ligada —
 * o número é ignorado — pra não desligar quem ainda está com ele no Flagsmith.
 */
export interface ICoordly3DFlag {
  isEnabled: boolean;
}

const COORDLY_3D_FLAG_VALUE_PATTERN = /^test(?:-\d+)?$/;

export const parseCoordly3DFlag = (enabled?: boolean, value?: unknown): ICoordly3DFlag => ({
  isEnabled: !!enabled && COORDLY_3D_FLAG_VALUE_PATTERN.test(String(value ?? '').trim()),
});

export const COORDLY_3D_FLAG_OFF: ICoordly3DFlag = { isEnabled: false };

export type Coordly3DFallbackReason =
  | 'not-ifc'
  | 'no-webgpu'
  | 'flag-off';

export type Coordly3DDecision =
  | { target: 'coordly' }
  | { target: 'autodesk'; reason: Coordly3DFallbackReason };

export interface ICoordly3DInput {
  extension?: string;
  /**
   * Já convertido no nativo. A flag controla PEDIR render nativo, não abrir o
   * que já existe: o que foi convertido abre no nosso viewer mesmo com a flag
   * desligada — mandar para a Autodesk faria a tela de lá pedir uma conversão.
   */
  hasNativeRender?: boolean;
}

/**
 * Decide se o arquivo abre no nosso viewer 3D. Não decide de ONDE vem a
 * geometria: isso é sempre o backend (render nativo → artefatos no Blob), e o
 * gate de rollout de lá (tenants, extensão, tamanho) pode ainda rebaixar para o
 * Autodesk — a rota 3D trata isso pela resposta do render.
 *
 * Com a flag ligada todo .ifc abre aqui, já convertido pela Autodesk ou não: o
 * Autodesk é opção do usuário (toggle do cabeçalho), nunca o padrão.
 */
export const decideCoordly3DTarget = (
  flag: ICoordly3DFlag,
  hasWebGPU: boolean,
  input: ICoordly3DInput,
): Coordly3DDecision => {
  if ((input.extension || '').toLocaleLowerCase() !== COORDLY_3D_VIEWER_EXTENSION) {
    return { target: 'autodesk', reason: 'not-ifc' };
  }
  if (!hasWebGPU) { return { target: 'autodesk', reason: 'no-webgpu' }; }
  if (!flag.isEnabled && !input.hasNativeRender) { return { target: 'autodesk', reason: 'flag-off' }; }

  return { target: 'coordly' };
};

export const useCoordly3DViewerFeature = () => {
  const flag = useFeatureFlag(COORDLY_3D_VIEWER_FLAG);
  const coordly3DFlag = useMemo(
    () => parseCoordly3DFlag(flag?.enabled, flag?.value),
    [flag?.enabled, flag?.value],
  );
  const isCoordly3DViewerEnabled = coordly3DFlag.isEnabled;
  const [hasWebGPU, setHasWebGPU] = useState(false);

  // Aquece o probe: a decisão de abertura precisa dele resolvido ANTES de
  // navegar, e requestAdapter() é async. O resultado fica em state porque o
  // clique direito precisa decidir de forma síncrona (ver isCoordly3DViewerEligible).
  useEffect(() => {
    if (!isCoordly3DViewerEnabled) { return; }

    let alive = true;
    void isWebGPUAvailable().then((available) => {
      if (alive) { setHasWebGPU(available); }
    });

    return () => { alive = false; };
  }, [isCoordly3DViewerEnabled]);

  // Ordem do gate: ifc → WebGPU → flag.
  const decideCoordly3D = useCallback(async (input: ICoordly3DInput): Promise<Coordly3DDecision> => {
    if ((input.extension || '').toLocaleLowerCase() !== COORDLY_3D_VIEWER_EXTENSION) {
      return { target: 'autodesk', reason: 'not-ifc' };
    }
    return decideCoordly3DTarget(coordly3DFlag, await isWebGPUAvailable(), input);
  }, [coordly3DFlag]);

  /**
   * Versão síncrona do gate, para os pontos que não podem esperar o probe:
   * o clique direito precisa decidir dentro do próprio handler (um `await` aí
   * deixa a navegação cair depois do menu de contexto abrir) e o `href` do
   * nome do arquivo é calculado no render.
   *
   * Enquanto o probe não resolve, responde `false` e o arquivo segue o fluxo
   * Autodesk — mesmo fallback conservador do gate assíncrono.
   */
  const isCoordly3DViewerEligible = useCallback((extension?: string, hasNativeRender?: boolean) => (
    decideCoordly3DTarget(coordly3DFlag, hasWebGPU, { extension, hasNativeRender }).target === 'coordly'
  ), [coordly3DFlag, hasWebGPU]);

  // Antes do Flagsmith responder a flag lê como desligada. Quem decide motor por
  // ela (o federado) precisa saber a diferença entre "desligada" e "ainda não sei".
  const isFlagReady = !!useFlagsmith().initialised;

  return { isCoordly3DViewerEnabled, isFlagReady, decideCoordly3D, isCoordly3DViewerEligible };
};

/**
 * Extensões que o federado aceita incluir. Com a flag do viewer 3D ativa, só IFC:
 * o conversor automático da Autodesk está pausado até segunda ordem, então
 * qualquer outra extensão entraria como modelo que ninguém consegue renderizar.
 */
export const useFederatedUploadFilter = () => {
  const { isCoordly3DViewerEnabled } = useCoordly3DViewerFeature();

  const isUploadableExtension = useCallback((extension?: string) => (
    isCoordly3DViewerEnabled
      ? (extension || '').toLocaleLowerCase() === COORDLY_3D_VIEWER_EXTENSION
      : isValid3DExtension(extension || '')
  ), [isCoordly3DViewerEnabled]);

  return { isUploadableExtension, onlyIfc: isCoordly3DViewerEnabled };
};

const hasErrorCode = (err: unknown, code: string): boolean => !!(
  err as { errors?: { ErrorCode?: string }[] } | null
)?.errors?.some((error) => error.ErrorCode === code);

/** Render recusado por falta de créditos de conversão Autodesk. */
export const isLimitedResourcesError = (err: unknown): boolean => hasErrorCode(err, 'LIMITED_RESOURCES');

/**
 * O backend recusou o motor nativo e NÃO rebaixou sozinho para a Autodesk —
 * converter lá gasta crédito, então a escolha é do usuário.
 */
export const isNativeEngineUnavailableError = (err: unknown): boolean => hasErrorCode(err, 'NATIVE_ENGINE_UNAVAILABLE');

// Mesma divisão do backend (`RenderQuotaViewModel`):
// a cota da Autodesk é por tipo de render, e extensão fora das duas listas não tem teto.
const SIMPLE_RENDER_EXTENSIONS = ['dwg', 'dgn', 'dxf', 'skp'];
const COMPLEX_RENDER_EXTENSIONS = ['rvt', 'nwc', 'nwd', 'ifc', 'dwf', 'dwfx', 'rcp'];

export type RenderJobType = 'simple' | 'complex';

export const renderJobTypeOf = (extension?: string | null): RenderJobType | undefined => {
  const ext = extension?.toLowerCase().replace('.', '');
  if (!ext) { return undefined; }
  if (SIMPLE_RENDER_EXTENSIONS.includes(ext)) { return 'simple'; }
  if (COMPLEX_RENDER_EXTENSIONS.includes(ext)) { return 'complex'; }
  return undefined;
};

export interface IRenderQuota {
  /** `undefined` é sem teto. */
  limit?: number;
  used: number;
  /** `false` só quando o backend diz que a cota acabou. */
  hasResource: boolean;
}

export const UNLIMITED_RENDER_QUOTA: IRenderQuota = { limit: undefined, used: 0, hasResource: true };

/**
 * Cota da Autodesk para o arquivo, na mesma conta da trava do Render. Se a
 * consulta falhar, o aviso sai sem contagem e quem barra é o Render
 * (LIMITED_RESOURCES).
 */
export const loadRenderQuota = async (fileName?: string | null): Promise<IRenderQuota> => {
  if (!fileName) { return UNLIMITED_RENDER_QUOTA; }
  try {
    const { data } = await ViewerApi.getRenderQuota(fileName);
    const quota = data.data;
    return {
      limit: quota.Unlimited ? undefined : quota.Max ?? undefined,
      used: quota.Used,
      hasResource: quota.HasResource,
    };
  } catch {
    return UNLIMITED_RENDER_QUOTA;
  }
};

/** Props do `ProgressRenderBar` para a cota. */
export const renderQuotaBar = ({ limit, used }: IRenderQuota) => ({ maxLimits: limit, value: used });

/** O que a conversão Autodesk pedida soma no contador do tipo dela. */
export const renderJobIncrement = (extension?: string | null): IUserLimitsActive => {
  const jobType = renderJobTypeOf(extension);
  if (jobType === 'simple') { return { QtySimpleRenderJobs: 1 }; }
  if (jobType === 'complex') { return { QtyComplexRenderJobs: 1 }; }
  return {};
};

/**
 * `location.state` da tela Autodesk quando ela foi aberta porque o nosso viewer
 * 3D desistiu do arquivo: a tela pergunta antes de abrir ou converter lá.
 */
export interface INativeFallbackState {
  nativeFallback: string;
}

// Aceite da conversão Autodesk dado na lista antes de abrir a nova guia: a tela
// de lá consome e converte sem perguntar de novo. Vai no localStorage (não na
// URL) para um link copiado não converter sozinho; vale uma vez e por pouco tempo.
const AUTODESK_CONVERSION_CONFIRMED_KEY = 'autodeskConversionConfirmed';
const AUTODESK_CONVERSION_CONFIRMED_TTL = 60_000;

export const markAutodeskConversionConfirmed = (fileApiId: string) => {
  try {
    localStorage.setItem(AUTODESK_CONVERSION_CONFIRMED_KEY, JSON.stringify({ fileApiId, at: Date.now() }));
  } catch { /* sem storage a tela pergunta de novo */ }
};

export const consumeAutodeskConversionConfirmed = (fileApiId: string): boolean => {
  try {
    const raw = localStorage.getItem(AUTODESK_CONVERSION_CONFIRMED_KEY);
    if (!raw) { return false; }
    const mark = JSON.parse(raw) as { fileApiId?: string; at?: number };
    if (mark.fileApiId !== fileApiId) { return false; }
    localStorage.removeItem(AUTODESK_CONVERSION_CONFIRMED_KEY);
    return Date.now() - (mark.at ?? 0) < AUTODESK_CONVERSION_CONFIRMED_TTL;
  } catch {
    return false;
  }
};

export const isValid3DExtension = (extension: string) => {
  if (extensions3d.indexOf(extension.toLocaleLowerCase()) !== -1) {
    return true;
  }
  return false;
}

export const getFileExtension = (filename: string) => {
  var ext = /^.+\.([^.]+)$/.exec(filename);
  return ext == null ? "" : ext[1];
}
